import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CoreMenu } from '@core/types';
import { IUser } from '../../interfaces/user.interface';
import { ApiRoutes } from '../../routes/app.routes';
import { ActiveUser, isLearner, isStaff, isAdmin, hasPermission } from '../../utils/helpers.utils';
import { SchoolContextService } from './school-context.service';
import { DynamicLabelService } from './dynamic-label.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicMenuService {
  constructor(
    private schoolContextService: SchoolContextService,
    private dynamicLabelService: DynamicLabelService
  ) { }

  /**
   * Generate the dynamic menu based on school type and user role
   */
  generateMenu(): Observable<CoreMenu[]> {
    return this.dynamicLabelService.getAllLabels().pipe(
      map(labels => this.buildMenuWithLabels(labels))
    );
  }

  /**
   * Build the menu with dynamic labels
   */
  private buildMenuWithLabels(labels: any): CoreMenu[] {
    const user: IUser = ActiveUser();

    return [
      // Dashboard - Accessible to all authenticated users
      {
        id: 'Dashboard',
        title: 'Dashboard',
        translate: 'MENU.DASHBOARD',
        type: 'item' as const,
        icon: 'grid',
        url: 'app/dashboard',
        role: [ApiRoutes.api.statistics.all],
      },

      // Profile Section - Role-specific
      ...(isLearner() ? [{
        id: 'learner-profile',
        title: 'My Profile',
        translate: 'MENU.MY_PROFILE',
        type: 'item' as const,
        icon: 'user',
        url: 'app/learners/profile/' + user.id,
        role: [ApiRoutes.api.learner.retrieveSingle],
      }] : []),

      ...(isStaff() ? [{
        id: 'teacher-profile',
        title: 'My Profile',
        translate: 'MENU.MY_PROFILE',
        type: 'item' as const,
        icon: 'user',
        url: 'app/staff/profile/' + user.id,
        role: [ApiRoutes.api.teacher.retrieveSingle],
      }] : []),

      // Academic Management - Admin & Staff Access
      ...(isStaff() || isAdmin() ? [{
        id: 'academic-management',
        title: 'Academic Management',
        translate: 'MENU.ACADEMIC_MANAGEMENT',
        type: 'collapsible' as const,
        icon: 'book-open',
        role: [ApiRoutes.api.course.retrieve, ApiRoutes.api.classroom.retrieve],
        children: [
          ...(this.shouldShowClassrooms() ? [{
            id: 'classrooms',
            title: 'Classrooms',
            translate: 'MENU.CLASSROOMS',
            type: 'collapsible' as const,
            icon: 'home',
            role: [ApiRoutes.api.classroom.retrieve],
            children: [
              {
                id: 'classrooms-list',
                title: 'All Classrooms',
                translate: 'MENU.ALL_CLASSROOMS',
                type: 'item' as const,
                icon: 'home',
                url: 'app/classrooms',
                role: [ApiRoutes.api.classroom.retrieve],
              },
              hasPermission(ApiRoutes.api.classroom.edit.toUpperCase()) && {
                id: 'classroom-progression',
                title: 'Progression Config',
                translate: 'MENU.PROGRESSION_CONFIG',
                type: 'item' as const,
                icon: 'git-branch',
                url: 'app/classroom-progression/config',
                role: [ApiRoutes.api.classroom.edit],
              },
              {
                id: 'progression-hierarchy',
                title: 'Progression Hierarchy',
                translate: 'MENU.PROGRESSION_HIERARCHY',
                type: 'item' as const,
                icon: 'sitemap',
                url: 'app/classroom-progression/hierarchy',
                role: [ApiRoutes.api.classroom.retrieve],
              },
            ].filter(Boolean),
          }] : []),
          {
            id: 'courses',
            title: labels.subject,
            translate: 'MENU.COURSES',
            type: 'collapsible' as const,
            icon: 'book-open',
            role: [ApiRoutes.api.course.retrieve],
            children: [
              {
                id: 'courses-list',
                title: labels.subject,
                translate: 'MENU.COURSES',
                type: 'item' as const,
                icon: 'book-open',
                url: 'app/courses',
                role: [ApiRoutes.api.course.retrieve],
              },
              {
                id: 'credit-value-management',
                title: 'Credit Value Management',
                translate: 'MENU.CREDIT_VALUE_MANAGEMENT',
                type: 'item' as const,
                icon: 'pi pi-edit',
                url: 'app/courses/credit-values',
                role: [ApiRoutes.api.course.edit],
              },
            ],
          },
          hasPermission(ApiRoutes.api.course.assignCourses.toUpperCase()) && {
            id: 'course-assignment',
            title: 'Course Assignment',
            translate: 'MENU.COURSE_ASSIGNMENT',
            type: 'item' as const,
            icon: 'user-check',
            url: 'app/course-assignment',
            role: [ApiRoutes.api.course.assignCourses],
          },
          // Course Registration - Only show for universities
          // ...(this.shouldShowCourseRegistration() ? [{
          //   id: 'course-registration',
          //   title: labels.courseRegistration,
          //   type: 'item' as const,
          //   icon: 'bookmark',
          //   url: 'app/courses/register',
          //   role: [ApiRoutes.api.course.retrieve],
          // }] : []),
          {
            id: 'academic-level',
            title: labels.academicLevel,
            translate: 'MENU.ACADEMIC_LEVEL',
            type: 'item' as const,
            icon: 'list',
            url: 'app/academic-level',
            role: [ApiRoutes.api.academicLevel.retrieve],
          },
          // Semester/Term - Show based on school type
          ...(this.shouldShowSemesters() ? [{
            id: 'semester',
            title: labels.semester,
            translate: 'MENU.SEMESTER',
            type: 'item' as const,
            icon: 'file',
            url: 'app/semester',
            role: [ApiRoutes.api.semester.retrieve],
          }] : []),
          ...(this.shouldShowTerms() ? [{
            id: 'term',
            title: labels.term,
            translate: 'MENU.TERM',
            type: 'item' as const,
            icon: 'file',
            url: 'app/semester',
            role: [ApiRoutes.api.semester.retrieve], // Use semester route as fallback
          }] : []),
          // Sequences - Show for schools that use them
          ...(this.shouldShowSequences() ? [{
            id: 'sequence',
            title: labels.sequence,
            translate: 'MENU.SEQUENCE',
            type: 'item' as const,
            icon: 'sliders',
            url: 'app/sequence',
            role: [ApiRoutes.api.sequence.retrieve],
          }] : []),
          // Series - Hide for Primary schools
          ...(this.shouldShowSeries() ? [{
            id: 'series',
            title: 'Series',
            translate: 'MENU.SERIES',
            type: 'item' as const,
            icon: 'move',
            url: 'app/series',
            role: [ApiRoutes.api.series.retrieve],
          }] : []),
          // Specialization - Hide for Primary schools
          ...(this.shouldShowSpecialization() ? [{
            id: 'specialty',
            title: labels.specialization,
            translate: this.shouldShowFaculty(),
            type: 'item' as const,
            icon: 'tool',
            url: 'app/specialty',
            role: [ApiRoutes.api.specialization.retrieve],
          }] : []),
          // Departments - Hide for Primary schools
          ...(this.shouldShowDepartments() ? [{
            id: 'departments',
            title: labels.department,
            translate: 'MENU.DEPARTMENTS',
            type: 'item' as const,
            icon: 'sunset',
            url: 'app/departments',
            role: [ApiRoutes.api.department.retrieve],
          }] : []),
        ]
      }] : []),

      // Teaching & Learning - Accessible to Staff & Learners
      ...(isStaff() || isLearner() || isAdmin() ? [{
        id: 'teaching-learning',
        title: 'Teaching & Learning',
        translate: 'MENU.TEACHING_LEARNING',
        type: 'collapsible' as const,
        icon: 'graduation-cap',
        role: [ApiRoutes.api.lecture.retrieve, ApiRoutes.api.assignment.retrieve, ApiRoutes.api.timeTable.retrieve],
        children: [
          // Student Attendance - Staff & Admin access
          ...(isStaff() || isAdmin() ? [{
            id: 'attendance',
            title: 'Attendance',
            translate: 'ATTENDANCE.TITLE',
            type: 'collapsible' as const,
            icon: 'check-square',
            role: [ApiRoutes.api.classroom.retrieve],
            children: [
              {
                id: 'attendance-dashboard',
                title: 'Dashboard',
                translate: 'ATTENDANCE.DASHBOARD.TITLE',
                type: 'item' as const,
                icon: 'grid',
                url: 'app/attendance',
                role: [ApiRoutes.api.classroom.retrieve],
              },
              {
                id: 'mark-attendance',
                title: 'Mark Attendance',
                translate: 'ATTENDANCE.MARK.TITLE',
                type: 'item' as const,
                icon: 'edit',
                url: 'app/attendance/mark',
                role: [ApiRoutes.api.classroom.retrieve],
              },
              {
                id: 'attendance-reports',
                title: 'Reports',
                translate: 'ATTENDANCE.REPORT.TITLE',
                type: 'item' as const,
                icon: 'file-text',
                url: 'app/attendance/report',
                role: [ApiRoutes.api.classroom.retrieve],
              },
            ],
          }] : []),
          // Assignments - Enhanced with submissions
          {
            id: 'assignments',
            title: 'Assignments',
            translate: 'MENU.ASSIGNMENTS',
            type: 'collapsible' as const,
            icon: 'activity',
            role: [ApiRoutes.api.assignment.retrieve],
            children: [
              {
                id: 'assignments-list',
                title: 'All Assignments',
                translate: 'MENU.ASSIGNMENTS',
                type: 'item' as const,
                icon: 'list',
                url: 'app/assignments',
                role: [ApiRoutes.api.assignment.retrieve],
              },
              ...(isStaff() || isAdmin() ? [{
                id: 'assignment-submissions',
                title: 'Submissions & Grading',
                translate: 'ASSIGNMENTS_ENHANCED.SUBMISSIONS.TITLE',
                type: 'item' as const,
                icon: 'check-circle',
                url: 'app/assignments-enhanced',
                role: [ApiRoutes.api.assignment.retrieve],
              }] : []),
            ],
          },
          // Lectures - Enhanced with materials
          {
            id: 'lectures',
            title: 'Lectures',
            translate: 'MENU.LECTURES',
            type: 'collapsible' as const,
            icon: 'book',
            role: [ApiRoutes.api.lecture.retrieve],
            children: [
              {
                id: 'lectures-list',
                title: 'All Lectures',
                translate: 'MENU.LECTURES',
                type: 'item' as const,
                icon: 'book',
                url: 'app/lectures',
                role: [ApiRoutes.api.lecture.retrieve],
              },
              ...(isStaff() || isAdmin() ? [{
                id: 'lecture-materials',
                title: 'Materials & Notes',
                translate: 'LECTURES_ENHANCED.MATERIALS.TITLE',
                type: 'item' as const,
                icon: 'file',
                url: 'app/lectures-enhanced',
                role: [ApiRoutes.api.lecture.retrieve],
              }] : []),
            ],
          },
          {
            id: 'timetable',
            title: 'Timetable',
            translate: 'MENU.TIMETABLE',
            type: 'item' as const,
            icon: 'layout',
            url: 'app/timetable',
            role: [ApiRoutes.api.timeTable.retrieve],
          },
          hasPermission(ApiRoutes.api.evaluation.sequenceEvaluations.toUpperCase()) && {
            id: 'evaluations',
            title: labels.evaluation,
            translate: 'MENU.EVALUATIONS',
            type: 'item' as const,
            icon: 'edit-2',
            url: 'app/evaluations',
            role: [ApiRoutes.api.evaluation.retrieve],
          },
          // Custom Exams - Admin & Staff Access (Single item pointing to dashboard)
          (isAdmin() || isStaff()) && {
            id: 'custom-exams',
            title: 'Custom Exams',
            translate: 'MENU.CUSTOM_EXAMS',
            type: 'item' as const,
            icon: 'file-text',
            url: 'app/custom-exam',
            role: [ApiRoutes.api.customExam.getCustomExams],
          },
          // NEED TO UPDATE THIS TO CHECK IF THE USER HAS THE PERMISSION TO SEE THE GRADEBOOK
          isAdmin() && {
            id: 'gradebook',
            title: 'Gradebook',
            translate: 'MENU.GRADEBOOK',
            type: 'item' as const,
            icon: 'clipboard',
            url: 'app/evaluations/gradebook',
            role: [ApiRoutes.api.evaluation.retrieve],
          },
          isAdmin() && {
            id: 'results',
            title: 'Results',
            translate: 'MENU.RESULTS',
            type: 'item' as const,
            icon: 'clipboard',
            url: 'app/results',
            role: [ApiRoutes.api.result.getTermPerformanceSummary],
          },
        ]
      }] : []),

      // Student Management - Admin & Staff Access
      ...(isAdmin() ? [{
        id: 'student-management',
        title: `${labels.student} Management`,
        translate: 'MENU.STUDENT_MANAGEMENT',
        type: 'collapsible' as const,
        icon: 'users',
        role: [ApiRoutes.api.learner.retrieve, ApiRoutes.api.teacher.retrieve],
        children: [
          hasPermission(ApiRoutes.api.learner.retrieve.toUpperCase()) && {
            id: 'learners',
            title: labels.student + 's',
            translate: 'MENU.STUDENTS',
            type: 'item' as const,
            icon: 'user-check',
            url: 'app/learners',
            role: [ApiRoutes.api.learner.retrieve],
          },
          // Student Registration - Only show for universities
          ...(this.shouldShowStudentRegistration() ? [
            hasPermission(ApiRoutes.api.learner.add.toUpperCase()) && {
              id: 'student-registration',
              title: labels.studentRegistration,
              translate: 'MENU.STUDENT_REGISTRATION',
              type: 'item' as const,
              icon: 'user-plus',
              url: 'app/register-student',
              role: [ApiRoutes.api.learner.add],
            }] : []),
          hasPermission(ApiRoutes.api.teacher.retrieve.toUpperCase()) && {
            id: 'staff',
            title: 'Staff',
            translate: 'MENU.STAFF',
            type: 'item' as const,
            icon: 'users',
            url: 'app/staff',
            role: [ApiRoutes.api.teacher.retrieve],
          },
        ]
      }] : []),

      // Administrative - Admin Only Access
      ...(isAdmin() ? [{
        id: 'administrative',
        title: 'Administrative',
        translate: 'MENU.ADMINISTRATIVE',
        type: 'collapsible' as const,
        icon: 'settings',
        role: [ApiRoutes.api.role.retrieve, ApiRoutes.api.fees.retrieve],
        children: [
          {
            id: 'roles',
            title: 'Roles & Permissions',
            translate: 'MENU.ROLES_PERMISSIONS',
            type: 'item' as const,
            icon: 'refresh-ccw',
            url: 'app/roles',
            role: [ApiRoutes.api.role.retrieve],
          },
          {
            id: 'academic-year',
            title: 'Academic Year',
            translate: 'MENU.ACADEMIC_YEAR',
            type: 'item' as const,
            icon: 'calendar',
            url: 'app/academic-year',
            role: [ApiRoutes.api.academicYear.retrieve],

          },
          ...(this.shouldShowPromotion() ? [
            hasPermission(ApiRoutes.api.promotion.promoteClass.toUpperCase()) && {
              id: 'student-promotion',
              title: 'Student Promotion',
              translate: 'MENU.STUDENT_PROMOTION',
              type: 'collapsible' as const,
              icon: 'graduation',
              role: [ApiRoutes.api.promotion.promoteClass],
              children: [
                {
                  id: 'promotion-dashboard',
                  title: 'Promotion Dashboard',
                  translate: 'MENU.PROMOTION_DASHBOARD',
                  type: 'item' as const,
                  icon: 'grid',
                  url: 'app/promotion',
                  role: [ApiRoutes.api.promotion.promoteClass],
                },
                {
                  id: 'progression-wizard',
                  title: 'Progression Wizard',
                  translate: 'MENU.PROGRESSION_WIZARD',
                  type: 'item' as const,
                  icon: 'zap',
                  url: 'app/classroom-progression/wizard',
                  role: [ApiRoutes.api.classroom.edit],
                },
                {
                  id: 'progression-analytics',
                  title: 'Progression Analytics',
                  translate: 'MENU.PROGRESSION_ANALYTICS',
                  type: 'item' as const,
                  icon: 'bar-chart-2',
                  url: 'app/classroom-progression/analytics',
                  role: [ApiRoutes.api.classroom.retrieve],
                },
              ]
            }] : []),
          // Staff Attendance - Admin only
          {
            id: 'staff-attendance',
            title: 'Staff Attendance',
            translate: 'STAFF_ATTENDANCE.TITLE',
            type: 'collapsible' as const,
            icon: 'clock',
            role: [ApiRoutes.api.teacher.retrieve],
            children: [
              {
                id: 'staff-attendance-dashboard',
                title: 'Dashboard',
                translate: 'STAFF_ATTENDANCE.DASHBOARD.TITLE',
                type: 'item' as const,
                icon: 'grid',
                url: 'app/staff-attendance',
                role: [ApiRoutes.api.teacher.retrieve],
              },
              {
                id: 'leave-management',
                title: 'Leave Management',
                translate: 'STAFF_ATTENDANCE.LEAVE.TITLE',
                type: 'item' as const,
                icon: 'calendar',
                url: 'app/staff-attendance/leave',
                role: [ApiRoutes.api.teacher.retrieve],
              },
            ],
          },
          // Fee Management - Enhanced with Mobile Money
          {
            id: 'fee-management',
            title: 'Fee Management',
            translate: 'FEES_ENHANCED.TITLE',
            type: 'collapsible' as const,
            icon: 'dollar-sign',
            role: [ApiRoutes.api.fees.retrieve],
            children: [
              {
                id: 'fees-dashboard',
                title: 'Dashboard',
                translate: 'FEES_ENHANCED.DASHBOARD.TITLE',
                type: 'item' as const,
                icon: 'pie-chart',
                url: 'app/fees-enhanced',
                role: [ApiRoutes.api.fees.retrieve],
              },
              {
                id: 'record-payment',
                title: 'Record Payment',
                translate: 'FEES_ENHANCED.PAYMENT.TITLE',
                type: 'item' as const,
                icon: 'credit-card',
                url: 'app/fees-enhanced/payment',
                role: [ApiRoutes.api.fees.feePayment],
              },
            ],
          },
          {
            id: 'account-settings',
            title: 'System Settings',
            translate: 'MENU.SYSTEM_SETTINGS',
            type: 'item' as const,
            icon: 'settings',
            url: 'app/admin/settings',
            role: [ApiRoutes.api.user.retrieveSingle],
          },
        ]
      }] : []),
    ];
  }

  /**
   * Check if course registration should be shown
   */
  private shouldShowCourseRegistration(): boolean {
    // Use synchronous method for immediate boolean return
    return this.schoolContextService.getFeatureSync('requiresCourseRegistration');
  }

  /**
   * Check if student registration should be shown
   */
  private shouldShowStudentRegistration(): boolean {
    // Use synchronous method for immediate boolean return
    return this.schoolContextService.getFeatureSync('requiresStudentRegistration');
  }

  /**
   * Check if semesters should be shown
   */
  private shouldShowSemesters(): boolean {
    // Use synchronous method for immediate boolean return
    return this.schoolContextService.getFeatureSync('usesSemesters');
  }

  /**
   * Check if terms should be shown
   */
  private shouldShowTerms(): boolean {
    // Use synchronous method for immediate boolean return
    return this.schoolContextService.getFeatureSync('usesTerms');
  }

  /**
   * Check if sequences should be shown
   */
  private shouldShowSequences(): boolean {
    // Use synchronous method for immediate boolean return
    return this.schoolContextService.getFeatureSync('usesSequences');
  }


  private shouldShowFaculty() {
    if (this.schoolContextService.isUniversity()) {
      return 'MENU.FACULTY'
    }
    else {
      return 'MENU.SPECIALTY'
    }

  }

  /**
   * Check if promotion should be shown for staff members
   */
  private shouldShowPromotion(): boolean {
    return this.schoolContextService.getFeatureSync('usesPromotion');
  }

  /***
   * Check if Classrooms should be shown
   */
  private shouldShowClassrooms(): boolean {
    return this.schoolContextService.getFeatureSync('usesClassrooms');
  }

  /**
   * Check if Series should be shown (hide for Primary schools)
   */
  private shouldShowSeries(): boolean {
    return !this.schoolContextService.isPrimarySchool();
  }

  /**
   * Check if Specialization should be shown (hide for Primary schools)
   */
  private shouldShowSpecialization(): boolean {
    return !this.schoolContextService.isPrimarySchool();
  }

  /**
   * Check if Departments should be shown (hide for Primary schools)
   */
  private shouldShowDepartments(): boolean {
    return !this.schoolContextService.isPrimarySchool();
  }

}
